import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { useI18n } from "@/contexts/I18nContext";

/**
 * STAS-only Portal Controls card.
 * Shown inside the dashboard for the root user only.
 */
export default function PortalControls() {
  const { theme, lang } = useI18n();
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState("");

  const portalUrl = `${window.location.origin}/portal`;

  useEffect(() => {
    api.get("/admin/portal/settings")
      .then(({ data }) => setSettings(data))
      .catch((e) => setErr(e?.response?.data?.detail || "error"));
  }, []);

  const update = async (patch) => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.patch("/admin/portal/settings", patch);
      setSettings(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "error");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(portalUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* ignore */ }
  };

  if (settings === null && !err) return null;

  const T = {
    ar: {
      title: "إدارة بوابة العميل (STAS)",
      url_label: "رابط البوابة",
      copy: "نسخ الرابط",
      copied: "تم النسخ ✓",
      open: "فتح في تبويب جديد",
      status: "حالة البوابة",
      enabled: "مُفعّلة",
      disabled: "متوقفة",
      turn_on: "تشغيل",
      turn_off: "إيقاف",
      mode_label: "وضع البوابة",
      mode_live: "مباشر",
      mode_preview: "معاينة",
      mode_test: "تجربة",
      desc_live: "البوابة منشورة للعملاء بشكل طبيعي.",
      desc_preview: "تظهر شارة (معاينة). للمراجعة قبل النشر.",
      desc_test: "تظهر شارة (تجربة). الطلبات تُعتبر اختبارية.",
    },
    en: {
      title: "Portal Controls (STAS)",
      url_label: "Portal URL",
      copy: "Copy link",
      copied: "Copied ✓",
      open: "Open in new tab",
      status: "Portal status",
      enabled: "Enabled",
      disabled: "Disabled",
      turn_on: "Turn on",
      turn_off: "Turn off",
      mode_label: "Portal mode",
      mode_live: "Live",
      mode_preview: "Preview",
      mode_test: "Test",
      desc_live: "Portal is live for clients.",
      desc_preview: "Preview banner is shown. For review before publishing.",
      desc_test: "Test banner is shown. Submissions are treated as test data.",
    },
  }[lang];

  const styles = {
    card: {
      background: theme.surfaceCard,
      border: `1px solid ${theme.borderAccent}`,
      borderRadius: "16px",
      padding: "22px",
      marginBottom: "20px",
    },
    title: { fontSize: "16px", fontWeight: 800, color: theme.gold, marginTop: 0, marginBottom: "16px" },
    section: { marginBottom: "16px" },
    label: { fontSize: "12px", color: theme.textMuted, marginBottom: "6px", letterSpacing: "0.4px" },
    urlRow: { display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "stretch" },
    urlBox: {
      flex: "1 1 240px",
      background: theme.inputBg,
      border: `1px solid ${theme.inputBorder}`,
      color: theme.text,
      padding: "10px 12px",
      borderRadius: "8px",
      fontSize: "13px",
      fontFamily: "monospace",
      direction: "ltr",
      textAlign: "left",
      wordBreak: "break-all",
    },
    btnGold: {
      background: theme.goldGradient,
      color: theme.name === "dark" ? "#0a0a0a" : "#ffffff",
      border: "none",
      padding: "10px 16px",
      borderRadius: "8px",
      fontWeight: 700,
      cursor: "pointer",
      fontSize: "13px",
    },
    btnGhost: {
      background: "transparent",
      color: theme.text,
      border: `1px solid ${theme.border}`,
      padding: "10px 16px",
      borderRadius: "8px",
      fontWeight: 700,
      cursor: "pointer",
      fontSize: "13px",
    },
    statusRow: { display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" },
    dot: (on) => ({
      width: "10px", height: "10px", borderRadius: "50%",
      background: on ? "#22c55e" : "#ef4444",
      boxShadow: `0 0 8px ${on ? "rgba(34,197,94,0.6)" : "rgba(239,68,68,0.6)"}`,
    }),
    modeGroup: { display: "flex", gap: "8px", flexWrap: "wrap" },
    modeBtn: (active) => ({
      padding: "8px 14px",
      borderRadius: "8px",
      border: `1px solid ${active ? theme.gold : theme.border}`,
      background: active
        ? (theme.name === "dark" ? "rgba(212,175,55,0.15)" : "rgba(184,144,42,0.15)")
        : "transparent",
      color: active ? theme.gold : theme.text,
      fontWeight: 700,
      cursor: "pointer",
      fontSize: "13px",
    }),
    desc: { fontSize: "12px", color: theme.textMuted, marginTop: "6px" },
    error: { color: theme.errorText, fontSize: "12px", marginTop: "8px" },
  };

  const isEnabled = settings?.enabled !== false;
  const mode = settings?.mode || "live";

  return (
    <div style={styles.card} data-testid="portal-controls">
      <h3 style={styles.title}>{T.title}</h3>

      <div style={styles.section}>
        <div style={styles.label}>{T.url_label}</div>
        <div style={styles.urlRow}>
          <div style={styles.urlBox} data-testid="portal-url-box">{portalUrl}</div>
          <button onClick={copy} style={styles.btnGold} data-testid="portal-copy-button">
            {copied ? T.copied : T.copy}
          </button>
          <a href={portalUrl} target="_blank" rel="noreferrer" style={{ ...styles.btnGhost, textDecoration: "none", display: "inline-flex", alignItems: "center" }} data-testid="portal-open-button">
            {T.open}
          </a>
        </div>
      </div>

      <div style={styles.section}>
        <div style={styles.label}>{T.status}</div>
        <div style={styles.statusRow}>
          <span style={styles.dot(isEnabled)} />
          <span style={{ fontWeight: 700 }} data-testid="portal-status-label">
            {isEnabled ? T.enabled : T.disabled}
          </span>
          <button
            onClick={() => update({ enabled: !isEnabled })}
            disabled={busy}
            style={isEnabled ? styles.btnGhost : styles.btnGold}
            data-testid="portal-toggle-enabled"
          >
            {isEnabled ? T.turn_off : T.turn_on}
          </button>
        </div>
      </div>

      <div style={styles.section}>
        <div style={styles.label}>{T.mode_label}</div>
        <div style={styles.modeGroup}>
          {[
            { key: "live", label: T.mode_live, desc: T.desc_live },
            { key: "preview", label: T.mode_preview, desc: T.desc_preview },
            { key: "test", label: T.mode_test, desc: T.desc_test },
          ].map((m) => (
            <button
              key={m.key}
              onClick={() => update({ mode: m.key })}
              disabled={busy}
              style={styles.modeBtn(mode === m.key)}
              data-testid={`portal-mode-${m.key}`}
            >
              {m.label}
            </button>
          ))}
        </div>
        <div style={styles.desc}>
          {mode === "live" && T.desc_live}
          {mode === "preview" && T.desc_preview}
          {mode === "test" && T.desc_test}
        </div>
      </div>

      {err && <div style={styles.error}>{err}</div>}
    </div>
  );
}
